import React from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rn-vui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../theme/rewardsTheme';

import Button from './Button';

const meta = {
  title: 'Components/Button',
  component: Button,
  argTypes: {
    title: {
      control: 'text',
    },
    variant: {
      control: 'select',
      options: ['text', 'outlined', 'contained'],
    },
    secondary: {
      control: 'boolean',
    },
    disabled: {
      control: 'boolean',
    },
    loading: {
      control: 'boolean',
    },
    onPress: {action: 'pressed'},
  },
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof Button>;

export default meta;

type Story = StoryObj<typeof meta>;

export const AllVariants: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Button title="Primary Button" onPress={() => console.log('Primary pressed')} />
      <Button title="Secondary Button" secondary onPress={() => console.log('Secondary pressed')} />
      <Button
        title="Outlined Button"
        variant="outlined"
        onPress={() => console.log('Outlined pressed')}
      />
      <Button title="Text Button" variant="text" onPress={() => console.log('Text pressed')} />
      <Button title="Disabled Button" disabled onPress={() => console.log('Should not fire')} />
      <Button title="Loading Button" loading onPress={() => console.log('Loading')} />
    </View>
  ),
};

export const Primary: Story = {
  args: {
    title: 'Continue',
    onPress: () => console.log('Continue pressed'),
  },
};

export const Secondary: Story = {
  args: {
    title: 'Go Back',
    secondary: true,
    onPress: () => console.log('Go back pressed'),
  },
};

export const Outlined: Story = {
  args: {
    title: 'Close',
    variant: 'outlined',
    onPress: () => console.log('Close pressed'),
  },
};

export const TextVariant: Story = {
  args: {
    title: 'Text Button',
    variant: 'text',
    onPress: () => console.log('Text button pressed'),
  },
};

export const Disabled: Story = {
  args: {
    title: 'Disabled Button',
    disabled: true,
    onPress: () => console.log('Should not fire'),
  },
};

export const Loading: Story = {
  args: {
    title: 'Processing...',
    loading: true,
    onPress: () => console.log('Loading'),
  },
};

export const LoadingDisabled: Story = {
  args: {
    title: 'Submitting',
    loading: true,
    disabled: true,
    onPress: () => console.log('Should not fire'),
  },
};

export const DisabledOutlined: Story = {
  args: {
    title: 'Disabled Outlined',
    variant: 'outlined',
    disabled: true,
    onPress: () => console.log('Should not fire'),
  },
};

export const DisabledSecondary: Story = {
  args: {
    title: 'Disabled Secondary',
    secondary: true,
    disabled: true,
    onPress: () => console.log('Should not fire'),
  },
};

export const DisabledText: Story = {
  args: {
    title: 'Disabled Text',
    variant: 'text',
    disabled: true,
    onPress: () => console.log('Should not fire'),
  },
};

export const AllDisabledVariants: Story = {
  render: () => (
    <View style={styles.variantsContainer}>
      <Button title="Disabled Primary" disabled onPress={() => {}} />
      <Button title="Disabled Secondary" secondary disabled onPress={() => {}} />
      <Button title="Disabled Outlined" variant="outlined" disabled onPress={() => {}} />
      <Button title="Disabled Text" variant="text" disabled onPress={() => {}} />
    </View>
  ),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
  },
  variantsContainer: {
    gap: 8,
  },
});
